<template>
  <b-card>
    <b-overlay
      :show="isLoadingSuggestion || isSaving"
      rounded="sm"
    >
      <div class="d-flex align-items-center justify-content-between mb-1">
        <h4 class="mb-0">
          {{ currentRt.topic_name || currentRt.autotopic_title || 'Тема' }}
          <b-badge variant="light-primary" class="ml-50">
            {{ formatLabel(currentRt.question_format) }}
          </b-badge>
        </h4>
        <game-builder-slot-panel
          v-if="(currentRt.required_questions || 5) > 1"
          :current-rt="currentRt"
          :current-slot="currentSlot"
          class="mx-1 flex-grow-1"
          @slot-clicked="onSlotClicked"
        />
        <b-badge variant="light-secondary">
          {{ acceptedQuestions.length }}/{{ currentRt.required_questions || 5 }}
        </b-badge>
      </div>

      <!-- Принятые вопросы: раскрываются в редактор частей -->
      <div
        v-for="question in acceptedQuestions"
        :key="question.id"
        class="mb-1"
      >
        <div
          class="d-flex align-items-center justify-content-between accepted-question-row"
          @click="toggleExpanded(question.id)"
        >
          <span>
            <b-badge variant="light-success" class="mr-50">{{ question.price }}</b-badge>
            {{ question.answer }}
          </span>
          <span>
            <feather-icon :icon="expandedId === question.id ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
            <b-button
              variant="flat-danger"
              size="sm"
              title="Удалить вопрос"
              @click.stop="$emit('question-deleted', question.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </span>
        </div>
        <question-parts-editor
          v-if="expandedId === question.id"
          :question="question"
          :api="makePartsApi(question.id)"
          class="mt-1"
          @updated="$emit('question-updated')"
        />
      </div>

      <hr>

      <!-- Ручной выбор артиста: доступен, пока тема не заполнена, независимо от наличия предложения -->
      <div v-if="!isTopicComplete" class="d-flex mb-1" style="gap: 8px">
        <template v-if="isPair">
          <b-button variant="outline-primary" size="sm" @click="openPickModal('audio')">
            Выбрать звучащего
          </b-button>
          <b-button variant="outline-primary" size="sm" @click="openPickModal('image')">
            Выбрать картиночного
          </b-button>
        </template>
        <b-button v-else variant="outline-primary" size="sm" @click="openPickModal(null)">
          Выбрать
        </b-button>
      </div>

      <game-builder-pick-modal
        v-model="pickModalVisible"
        :game-id="gameId"
        :rt-id="currentRt.id"
        :current-rt="currentRt"
        entity-kind="artist"
        :side="pickSide"
        :pick-for="pickFor"
        @picked="onPicked"
      />

      <!-- Предложение нового вопроса -->
      <div v-if="suggestion">
        <h5 class="mb-1">
          {{ suggestionTitle }}
        </h5>

        <!-- music / cover / cover_arts / music_choice / пары / missing_consonants: аудио с нарезкой -->
        <div v-if="suggestion.track || suggestion.cover_track">
          <p
            v-if="suggestion.format === 'cover'"
            class="font-italic"
          >
            {{ suggestion.intro_text }} (оригинал: {{ suggestion.original_track.title }})
          </p>
          <p class="mb-50">
            <strong>{{ audioTrack.title }}</strong>
            <small class="text-muted">({{ audioTrack.play_count || 0 }} прослушиваний)</small>
          </p>
          <img
            v-if="suggestion.cover"
            :src="suggestion.cover.url"
            alt="Обложка"
            class="suggestion-image mb-1"
          >

          <!-- music_choice: варианты -->
          <b-list-group
            v-if="suggestion.format === 'music_choice'"
            class="mb-1"
            style="max-width: 420px"
          >
            <b-list-group-item
              v-for="option in localAnswerOptions"
              :key="option.label"
              :variant="option.label === suggestion.correct_option_label ? 'success' : ''"
            >
              <div class="d-flex align-items-center justify-content-between">
                <span><strong>{{ option.label }}.</strong> {{ option.text }}</span>
                <span v-if="option.label !== suggestion.correct_option_label" class="text-nowrap">
                  <b-button
                    variant="flat-secondary"
                    size="sm"
                    title="Подобрать автоматически"
                    :disabled="isReplacingOption"
                    @click="autoReplaceOption(option.label)"
                  >
                    <feather-icon icon="RefreshCwIcon" size="14" />
                  </b-button>
                  <b-button
                    variant="flat-secondary"
                    size="sm"
                    title="Выбрать вручную"
                    @click="openDistractorModal(option.label)"
                  >
                    <feather-icon icon="SearchIcon" size="14" />
                  </b-button>
                </span>
              </div>
            </b-list-group-item>
          </b-list-group>

          <!-- пары: подводка, стык, картинка (у cover свой абзац с "оригиналом" выше, второй раз не показываем) -->
          <p
            v-if="suggestion.intro_text && suggestion.format !== 'cover'"
            class="font-italic mb-50"
          >
            {{ suggestion.intro_text }}
          </p>
          <p
            v-if="suggestion.matched_text"
            class="text-muted mb-50"
          >
            Стык: «{{ suggestion.matched_text }}» — {{ suggestion.answer_preview }}
          </p>
          <img
            v-if="suggestion.image"
            :src="suggestion.image.url"
            alt="Картинка пары"
            class="suggestion-image mb-1"
          >

          <!-- missing_consonants: загадка -->
          <pre
            v-if="suggestion.puzzle_text"
            class="members-preview mb-1"
          >{{ suggestion.puzzle_text }}</pre>

          <strip-audio-cutter
            v-model="cut"
            :audio-url="audioTrack.url"
            :default-start="55"
            :default-duration="suggestion.duration || 25"
          />
        </div>

        <!-- artist_photo -->
        <div v-else-if="suggestion.format === 'artist_photo'">
          <img
            :src="suggestion.photo.url"
            alt="Фото артиста"
            class="suggestion-image"
          >
        </div>

        <!-- band_members -->
        <div v-else-if="suggestion.format === 'band_members'">
          <pre class="members-preview">{{ suggestion.members_text }}</pre>
        </div>

        <!-- gapoifika: загадка из строк нескольких треков -->
        <div v-else-if="suggestion.format === 'gapoifika'">
          <pre class="members-preview">{{ suggestion.lines.join('\n') }}</pre>
          <p class="text-muted mt-50 mb-0">
            {{ suggestion.tracks.map(track => track.title).join(' / ') }}
          </p>
        </div>

        <div class="d-flex mt-1" style="gap: 8px">
          <b-button
            variant="success"
            :disabled="isSaving"
            @click="acceptSuggestion"
          >
            Принять вопрос
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="isLoadingSuggestion"
            @click="$emit('skipped')"
          >
            Другой вариант
          </b-button>
        </div>
      </div>

      <div v-else-if="!isLoadingSuggestion && !isTopicComplete">
        <p v-if="suggestionRequested" class="text-muted my-2">
          Не удалось подобрать вопрос — попробуйте другую тему
        </p>
        <template v-else>
          <p class="text-muted my-2">
            Нажмите «Другой вариант» или «Выбрать», чтобы подобрать вопрос
          </p>
          <b-button
            variant="outline-secondary"
            :disabled="isLoadingSuggestion"
            @click="$emit('skipped')"
          >
            Другой вариант
          </b-button>
        </template>
      </div>

      <div class="mt-2">
        <b-button
          variant="outline-danger"
          size="sm"
          @click="$emit('topic-removed')"
        >
          Убрать тему
        </b-button>
      </div>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard, BOverlay, BButton, BBadge, BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import StripAudioCutter from '@/views/strip/builder/StripAudioCutter.vue'
import QuestionPartsEditor from '@/views/question/QuestionPartsEditor.vue'
import GameBuilderSlotPanel from './GameBuilderSlotPanel.vue'
import GameBuilderPickModal from './GameBuilderPickModal.vue'
import { formatLabel, isPairFormat } from './gameBuilderMusicFormats'

export default {
  components: {
    BCard,
    BOverlay,
    BButton,
    BBadge,
    BListGroup,
    BListGroupItem,
    StripAudioCutter,
    QuestionPartsEditor,
    GameBuilderSlotPanel,
    GameBuilderPickModal,
  },
  props: {
    gameId: { type: [Number, String], required: true },
    currentRt: { type: Object, required: true },
    suggestion: { type: Object, default: null },
    isLoadingSuggestion: { type: Boolean, default: false },
    suggestionRequested: { type: Boolean, default: false },
    isSaving: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const toast = useToast()
    const expandedId = ref(null)
    const cut = ref({ startSeconds: 55, durationSeconds: 25 })
    const targetSlot = ref(null) // слот, выбранный для принимаемого вопроса
    const pickModalVisible = ref(false)
    const pickSide = ref(null) // 'audio' | 'image' | null — для парных форматов
    const pickFor = ref('question') // 'question' | 'distractor' — режим модалки выбора
    // Локальная копия вариантов music_choice: заменяем дистракторы, не мутируя props.suggestion
    const localAnswerOptions = ref([])
    const isReplacingOption = ref(false)
    const replaceLabel = ref(null) // вариант, который меняем через модалку ручного выбора

    const acceptedQuestions = computed(() => props.currentRt.questions || [])
    const isTopicComplete = computed(
      () => acceptedQuestions.value.length >= (props.currentRt.required_questions || 5),
    )
    const isPair = computed(() => isPairFormat(props.currentRt.question_format))

    const firstFreeSlot = computed(() => {
      const required = props.currentRt.required_questions || 5
      const occupied = acceptedQuestions.value.map(question => question.order)
      for (let number = 1; number <= required; number += 1) {
        if (!occupied.includes(number)) return number
      }
      return null
    })

    // Текущий слот: у раскрытого вопроса — его слот, иначе цель для нового
    const currentSlot = computed(() => {
      if (expandedId.value) {
        const expanded = acceptedQuestions.value.find(question => question.id === expandedId.value)
        return expanded ? expanded.order : null
      }
      return targetSlot.value || firstFreeSlot.value
    })

    // Трек, который звучит в вопросе (для cover — кавер-трек)
    const audioTrack = computed(() => {
      if (!props.suggestion) return {}
      return props.suggestion.cover_track || props.suggestion.track || {}
    })

    // Заголовок предложения: у обычных форматов есть artist, у пар — только answer_preview
    const suggestionTitle = computed(() => {
      const s = props.suggestion
      if (!s) return ''
      if (s.artist) return s.artist.title
      if (s.answer_preview) return s.answer_preview
      return ''
    })

    watch(() => props.suggestion, suggestion => {
      cut.value = {
        startSeconds: 55,
        durationSeconds: (suggestion && suggestion.duration) || 25,
      }
      // После перезагрузки предложения цель слота сбрасывается на первый свободный
      targetSlot.value = null
      // Локальная копия вариантов music_choice — на новом предложении собираем заново
      localAnswerOptions.value = suggestion && suggestion.answer_options
        ? suggestion.answer_options.map(option => ({ ...option }))
        : []
    })

    // После смены темы цель слота и раскрытый вопрос сбрасываются
    watch(() => props.currentRt.id, () => {
      targetSlot.value = null
      expandedId.value = null
    })

    const toggleExpanded = questionId => {
      expandedId.value = expandedId.value === questionId ? null : questionId
    }

    // Открыть модалку ручного выбора артиста (side — для парных форматов)
    const openPickModal = side => {
      pickSide.value = side
      pickFor.value = 'question'
      pickModalVisible.value = true
    }

    const showToast = (title, variant = 'warning') => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant },
    })

    // Подставить новый текст дистрактора по его label, не трогая остальные варианты
    const setOptionText = (label, text) => {
      localAnswerOptions.value = localAnswerOptions.value.map(option => (
        option.label === label ? { ...option, text } : option
      ))
    }

    const hasDuplicateText = text => localAnswerOptions.value.some(
      option => option.text.trim().toLowerCase() === text.trim().toLowerCase(),
    )

    // Автоматический подбор нового дистрактора вместо указанного варианта
    const autoReplaceOption = label => {
      isReplacingOption.value = true
      store.dispatch('game-builder/suggestDistractor', {
        gameId: props.gameId,
        rtId: props.currentRt.id,
        params: {
          artist_id: props.suggestion.artist.id,
          exclude: localAnswerOptions.value.map(option => option.text),
        },
      })
        .then(response => {
          const data = response.data.data || response.data
          setOptionText(label, data.text)
          isReplacingOption.value = false
        })
        .catch(error => {
          isReplacingOption.value = false
          showToast(error.response?.data?.message || 'Не удалось подобрать вариант')
        })
    }

    // Ручной выбор дистрактора через модалку поиска артистов
    const openDistractorModal = label => {
      replaceLabel.value = label
      pickFor.value = 'distractor'
      pickSide.value = null
      pickModalVisible.value = true
    }

    // Артист выбран вручную: для дистрактора подставляем текст в вариант, иначе — обычный флоу подбора темы
    const onPicked = ({ id, title }) => {
      if (pickFor.value === 'distractor') {
        if (hasDuplicateText(title)) {
          showToast('Такой вариант уже есть')
          return
        }
        setOptionText(replaceLabel.value, title)
        return
      }
      emit('suggest-picked', { artist_id: id, side: pickSide.value || undefined })
    }

    const onSlotClicked = slotNumber => {
      if (expandedId.value) {
        const expanded = acceptedQuestions.value.find(question => question.id === expandedId.value)
        if (expanded && expanded.order !== slotNumber) {
          emit('question-slot-changed', { questionId: expandedId.value, slot: slotNumber })
        }
        return
      }
      targetSlot.value = slotNumber
    }

    const acceptSuggestion = () => {
      const s = props.suggestion
      // У парных форматов нет s.artist — есть audio_artist (артист звучащего трека)
      const payload = { artist_id: s.artist ? s.artist.id : s.audio_artist.id }

      if (s.format === 'music') {
        payload.track_id = s.track.id
        payload.start_seconds = cut.value.startSeconds
        payload.duration_seconds = cut.value.durationSeconds
      } else if (s.format === 'cover') {
        payload.original_track_id = s.original_track.id
        payload.cover_track_id = s.cover_track.id
        payload.intro_text = s.intro_text
        payload.start_seconds = cut.value.startSeconds
        payload.duration_seconds = cut.value.durationSeconds
      } else if (s.format === 'cover_arts') {
        payload.cover_id = s.cover.id
        payload.track_id = s.track.id
        payload.start_seconds = cut.value.startSeconds
        payload.duration_seconds = cut.value.durationSeconds
      } else if (s.format === 'artist_photo') {
        payload.photo_id = s.photo.id
      } else if (s.format === 'music_choice') {
        payload.track_id = s.track.id
        payload.start_seconds = cut.value.startSeconds
        payload.duration_seconds = cut.value.durationSeconds
        payload.answer_options = localAnswerOptions.value
        payload.correct_option_label = s.correct_option_label
      } else if (s.format === 'gapoifika') {
        payload.artist_id = s.artist.id
        payload.track_ids = s.tracks.map(track => track.id)
      } else if (s.format === 'missing_consonants') {
        payload.track_id = s.track.id
        payload.start_seconds = cut.value.startSeconds
        payload.duration_seconds = cut.value.durationSeconds
      } else if (s.pair_a) {
        // шесть парных форматов
        payload.artist_id = s.audio_artist.id
        payload.pair_a_id = s.pair_a.id
        payload.pair_b_id = s.pair_b.id
        payload.image_artist_id = s.image_artist.id
        payload.track_id = s.track.id
        if (s.image.kind === 'photo') {
          payload.photo_id = s.image.id
        } else {
          payload.cover_id = s.image.id
        }
        payload.start_seconds = cut.value.startSeconds
        payload.duration_seconds = cut.value.durationSeconds
      }
      // band_members — только artist_id

      // Слот для НОВОГО вопроса, а не currentSlot (тот при раскрытом/выбранном вопросе
      // указывает на слот РЕДАКТИРУЕМОГО вопроса — иначе принятие вытеснит его)
      const acceptSlot = targetSlot.value || firstFreeSlot.value
      if (acceptSlot) {
        payload.slot = acceptSlot
      }

      emit('accepted', payload)
    }

    // api редактора частей поверх game-builder-эндпоинтов
    const makePartsApi = questionId => ({
      updateQuestion: payload => store.dispatch('game-builder/updateBuilderQuestion', { gameId: props.gameId, questionId, payload }),
      updatePart: (partId, payload) => store.dispatch('game-builder/updateQuestionPart', { gameId: props.gameId, questionId, partId, payload }),
      deletePart: partId => store.dispatch('game-builder/deleteQuestionPart', { gameId: props.gameId, questionId, partId }),
      reorderParts: order => store.dispatch('game-builder/reorderQuestionParts', { gameId: props.gameId, questionId, order }),
      addPart: payload => store.dispatch('game-builder/addQuestionPart', { gameId: props.gameId, questionId, payload }),
      replaceAudioPart: (partId, payload) => store.dispatch('game-builder/replaceQuestionAudioPart', { gameId: props.gameId, questionId, partId, payload }),
      searchArtists: q => store.dispatch('game-builder/searchArtists', { q }).then(r => r.data?.data || r.data || []),
      fetchArtistTracks: artistId => store.dispatch('game-builder/fetchArtistTracks', { artistId }).then(r => r.data?.data || r.data || []),
    })

    return {
      expandedId,
      cut,
      acceptedQuestions,
      isTopicComplete,
      audioTrack,
      suggestionTitle,
      currentSlot,
      onSlotClicked,
      toggleExpanded,
      acceptSuggestion,
      makePartsApi,
      formatLabel,
      pickModalVisible,
      pickSide,
      pickFor,
      isPair,
      openPickModal,
      onPicked,
      localAnswerOptions,
      isReplacingOption,
      autoReplaceOption,
      openDistractorModal,
    }
  },
}
</script>

<style scoped>
.accepted-question-row {
  cursor: pointer;
  padding: 6px 8px;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
}
.suggestion-image {
  max-width: 320px;
  max-height: 320px;
  border-radius: 4px;
  display: block;
}
.members-preview {
  background: #f8f8f8;
  padding: 12px;
  border-radius: 4px;
  white-space: pre-wrap;
}
</style>
